"use client";

import { useState, useRef, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/components/ui/card";
import { Button } from "@/app/components/ui/button";
import { Textarea } from "@/app/components/ui/textarea";
import { Input } from "@/app/components/ui/input";
import { Label } from "@/app/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/components/ui/tabs";
import { Alert, AlertDescription } from "@/app/components/ui/alert";
import { Badge } from "@/app/components/ui/badge";
import { Progress } from "@/app/components/ui/progress";
import { Switch } from "@/app/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/components/ui/select";
import { 
  Copy, Download, Upload, FileText, Image, File, RotateCcw, CheckCircle, XCircle, 
  AlertTriangle, Hash, Shield, Key, Lock, Eye, EyeOff, Settings, Zap, FileCheck,
  FolderOpen, Hash as HashIcon, CheckSquare, Square, Loader2
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";

interface HashResult {
  input: string;
  hashes: Record<string, string>;
}

interface VerificationResult {
  input: string;
  algorithm: string;
  computed: string;
  expected: string;
  match: boolean;
}

export default function HashGeneratorPage() {
  const [activeTab, setActiveTab] = useState("generate");
  const [fileInput, setFileInput] = useState<File | null>(null);
  const [textInput, setTextInput] = useState("");
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<("md5" | "sha1" | "sha256" | "sha384" | "sha512" | "blake2b512" | "sha3-256" | "sha3-384" | "sha3-512")[]>(["sha256"]);
  const [encoding, setEncoding] = useState<"hex" | "base64" | "base64url">("hex");
  const [outputFormat, setOutputFormat] = useState<"raw" | "algo_hash" | "json" | "checksum" | "manifest">("json");
  const [uppercase, setUppercase] = useState(false);
  const [results, setResults] = useState<HashResult[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [progress, setProgress] = useState(0);

  // Advanced options
  const [normalizeUnicode, setNormalizeUnicode] = useState<"NFC" | "NFD" | "NFKC" | "NFKD" | "none">("none");
  const [normalizeLineEndings, setNormalizeLineEndings] = useState(false);
  const [stripBOM, setStripBOM] = useState(false);
  
  // HMAC options
  const [hmacEnabled, setHmacEnabled] = useState(false);
  const [hmacKey, setHmacKey] = useState("");
  const [showHmacKey, setShowHmacKey] = useState(false);
  
  // Verification
  const [verificationInput, setVerificationInput] = useState("");
  const [verificationResults, setVerificationResults] = useState<VerificationResult[]>([]);
  const [verificationMode, setVerificationMode] = useState<"single" | "manifest">("single");

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Available algorithms
  const algorithms = [
    { value: "md5" as const, label: "MD5", deprecated: true },
    { value: "sha1" as const, label: "SHA-1", deprecated: true },
    { value: "sha256" as const, label: "SHA-256" },
    { value: "sha384" as const, label: "SHA-384" },
    { value: "sha512" as const, label: "SHA-512" },
    { value: "blake2b512" as const, label: "BLAKE2b-512" },
    { value: "sha3-256" as const, label: "SHA3-256" },
    { value: "sha3-384" as const, label: "SHA3-384" },
    { value: "sha3-512" as const, label: "SHA3-512" },
  ];

  // tRPC hooks
  const generateHashesMutation = trpc.hash.generate.useMutation();
  const verifyHashesMutation = trpc.hash.verify.useMutation();
  const algorithmsQuery = trpc.hash.algorithms.useQuery();

  const handleAlgorithmToggle = (algorithm: "md5" | "sha1" | "sha256" | "sha384" | "sha512" | "blake2b512" | "sha3-256" | "sha3-384" | "sha3-512") => {
    setSelectedAlgorithms(prev => 
      prev.includes(algorithm) 
        ? prev.filter(a => a !== algorithm)
        : [...prev, algorithm]
    );
  };

  const generateRandomKey = () => {
    const key = crypto.getRandomValues(new Uint8Array(32));
    setHmacKey(btoa(String.fromCharCode(...key)));
  };

  const processFile = useCallback(async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as ArrayBuffer;
        const base64 = btoa(String.fromCharCode(...new Uint8Array(result)));
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }, []);

  const handleGenerateHashes = async () => {
    if (selectedAlgorithms.length === 0) {
      setErrorMessage("Please select at least one algorithm");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");
    setProgress(0);

    try {
      const inputs = [];

      // Add text input if provided
      if (textInput.trim()) {
        inputs.push({
          type: "text" as const,
          content: textInput,
          normalizeUnicode: normalizeUnicode === "none" ? undefined : normalizeUnicode,
          normalizeLineEndings: normalizeLineEndings,
          stripBOM: stripBOM,
        });
      }

      // Add file input if provided
      if (fileInput) {
        const fileContent = await processFile(fileInput);
        inputs.push({
          type: "file" as const,
          filename: fileInput.name,
          content: fileContent,
          normalizeLineEndings: normalizeLineEndings,
          stripBOM: stripBOM,
        });
      }

      if (inputs.length === 0) {
        setErrorMessage("Please provide text or upload a file");
        return;
      }

      setProgress(50);

      const result = await generateHashesMutation.mutateAsync({
        inputs,
        algorithms: selectedAlgorithms,
        encoding,
        outputFormat,
        uppercase,
        hmac: hmacEnabled ? {
          enabled: true,
          key: btoa(hmacKey),
        } : undefined,
      });

      setProgress(100);

      if (result.success) {
        if (outputFormat === "json" && result.data.results) {
          setResults(result.data.results);
        } else {
          // For other formats, create a simple result structure
          setResults([{
            input: "result",
            hashes: { output: result.data }
          }]);
        }
        toast.success("Hashes generated successfully!");
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to generate hashes");
      toast.error("Failed to generate hashes");
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleVerifyHashes = async () => {
    if (selectedAlgorithms.length === 0) {
      setErrorMessage("Please select at least one algorithm");
      return;
    }

    if (!verificationInput.trim()) {
      setErrorMessage("Please provide expected hash(es) for verification");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");

    try {
      const inputs = [];

      // Add text input if provided
      if (textInput.trim()) {
        inputs.push({
          type: "text" as const,
          content: textInput,
          normalizeUnicode: normalizeUnicode === "none" ? undefined : normalizeUnicode,
          normalizeLineEndings: normalizeLineEndings,
          stripBOM: stripBOM,
        });
      }

      // Add file input if provided
      if (fileInput) {
        const fileContent = await processFile(fileInput);
        inputs.push({
          type: "file" as const,
          filename: fileInput.name,
          content: fileContent,
          normalizeLineEndings: normalizeLineEndings,
          stripBOM: stripBOM,
        });
      }

      if (inputs.length === 0) {
        setErrorMessage("Please provide text or upload a file to verify");
        return;
      }

      let expectedHashes: string | Record<string, string>;
      
      if (verificationMode === "manifest") {
        try {
          expectedHashes = JSON.parse(verificationInput);
        } catch {
          setErrorMessage("Invalid JSON format for manifest");
          return;
        }
      } else {
        expectedHashes = verificationInput.trim();
      }

      const result = await verifyHashesMutation.mutateAsync({
        inputs,
        algorithms: selectedAlgorithms,
        expectedHashes,
        constantTime: true,
      });

      if (result.success) {
        setVerificationResults(result.data.results);
        if (result.data.success) {
          toast.success("All hashes match!");
        } else {
          toast.error(`${result.data.summary.mismatches} hash(es) do not match`);
        }
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Failed to verify hashes");
      toast.error("Failed to verify hashes");
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const downloadResults = () => {
    const content = JSON.stringify(results, null, 2);
    const blob = new Blob([content], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "hash-results.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setTextInput("");
    setFileInput(null);
    setResults([]);
    setVerificationResults([]);
    setErrorMessage("");
    setHmacKey("");
    setVerificationInput("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4 bg-linear-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          Advanced Hash Generator
        </h1>
        <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
          Generate cryptographic hashes with support for multiple algorithms, HMAC, KDF, 
          verification, and advanced features like Unicode normalization and streaming.
          </p>
        </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <HashIcon className="h-4 w-4" />
            Generate
          </TabsTrigger>
          <TabsTrigger value="verify" className="flex items-center gap-2">
            <CheckSquare className="h-4 w-4" />
            Verify
          </TabsTrigger>
          <TabsTrigger value="advanced" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Advanced
          </TabsTrigger>
          </TabsList>

        <TabsContent value="generate" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Input
                  </CardTitle>
                  <CardDescription>
                  Provide text or upload files to generate hashes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="text-input">Text Input</Label>
                  <Textarea
                    id="text-input"
                    placeholder="Enter text to hash..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="min-h-[120px]"
                  />
                </div>

                <div>
                  <Label htmlFor="file-input">File Upload</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      ref={fileInputRef}
                      id="file-input"
                      type="file"
                      onChange={(e) => setFileInput(e.target.files?.[0] || null)}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                  {fileInput && (
                    <div className="mt-2 flex items-center gap-2">
                      <File className="h-4 w-4" />
                      <span className="text-sm text-muted-foreground">
                        {fileInput.name} ({(fileInput.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Algorithm Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Algorithms
                </CardTitle>
                <CardDescription>
                  Select hash algorithms to use
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {algorithms.map((algo) => (
                    <div key={algo.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={algo.value}
                        checked={selectedAlgorithms.includes(algo.value)}
                        onChange={() => handleAlgorithmToggle(algo.value)}
                        className="rounded"
                      />
                      <Label htmlFor={algo.value} className="flex items-center gap-2">
                        {algo.label}
                        {algo.deprecated && (
                          <Badge variant="destructive" className="text-xs">
                            Legacy
                          </Badge>
                        )}
                      </Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Options */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="encoding">Encoding</Label>
                  <Select value={encoding} onValueChange={(value: any) => setEncoding(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hex">Hexadecimal</SelectItem>
                      <SelectItem value="base64">Base64</SelectItem>
                      <SelectItem value="base64url">Base64 URL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="format">Output Format</Label>
                  <Select value={outputFormat} onValueChange={(value: any) => setOutputFormat(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="raw">Raw Hash</SelectItem>
                      <SelectItem value="algo_hash">Algorithm:Hash</SelectItem>
                      <SelectItem value="checksum">Checksum File</SelectItem>
                      <SelectItem value="manifest">Manifest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="uppercase"
                    checked={uppercase}
                    onCheckedChange={setUppercase}
                  />
                  <Label htmlFor="uppercase">Uppercase</Label>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* HMAC Options */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                HMAC (Hash-based Message Authentication Code)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="hmac-enabled"
                    checked={hmacEnabled}
                    onCheckedChange={setHmacEnabled}
                  />
                  <Label htmlFor="hmac-enabled">Enable HMAC</Label>
                </div>

                {hmacEnabled && (
                  <div className="space-y-2">
                    <Label htmlFor="hmac-key">HMAC Key</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="hmac-key"
                        type={showHmacKey ? "text" : "password"}
                        placeholder="Enter HMAC key..."
                        value={hmacKey}
                        onChange={(e) => setHmacKey(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowHmacKey(!showHmacKey)}
                      >
                        {showHmacKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </Button>
                        <Button
                          variant="outline"
                          size="sm"
                        onClick={generateRandomKey}
                        >
                        <Zap className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Generate Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleGenerateHashes}
              disabled={isProcessing || selectedAlgorithms.length === 0}
              size="lg"
              className="min-w-[200px]"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Hash className="h-4 w-4 mr-2" />
                  Generate Hashes
                </>
              )}
            </Button>
          </div>

          {isProcessing && (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Processing...</span>
                    <span>{progress}%</span>
                  </div>
                  <Progress value={progress} className="w-full" />
                </div>
              </CardContent>
            </Card>
          )}

          {errorMessage && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {/* Results */}
          {results.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    Results
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard(JSON.stringify(results, null, 2))}
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                      <Button
                        variant="outline"
                      size="sm"
                      onClick={downloadResults}
                      >
                        <Download className="h-4 w-4 mr-2" />
                      Download
                      </Button>
                  </div>
                </CardTitle>
                </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="space-y-2">
                      <h4 className="font-medium">{result.input}</h4>
                      <div className="grid gap-2">
                        {Object.entries(result.hashes).map(([algorithm, hash]) => (
                          <div key={algorithm} className="flex items-center justify-between p-2 bg-muted rounded">
                            <Badge variant="outline">{algorithm.toUpperCase()}</Badge>
                          <div className="flex items-center gap-2">
                              <code className="text-sm font-mono break-all">{hash}</code>
                          <Button
                            variant="ghost"
                            size="sm"
                                onClick={() => copyToClipboard(hash)}
                          >
                                <Copy className="h-3 w-3" />
                          </Button>
                            </div>
                        </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                </CardContent>
              </Card>
          )}
          </TabsContent>

        <TabsContent value="verify" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input Section (same as generate) */}
            <Card>
                <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Input to Verify
                </CardTitle>
                  <CardDescription>
                  Provide text or upload files to verify against expected hashes
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="verify-text-input">Text Input</Label>
                  <Textarea
                    id="verify-text-input"
                    placeholder="Enter text to verify..."
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="min-h-[120px]"
                  />
                </div>

                <div>
                  <Label htmlFor="verify-file-input">File Upload</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="verify-file-input"
                      type="file"
                      onChange={(e) => setFileInput(e.target.files?.[0] || null)}
                      className="flex-1"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => document.getElementById('verify-file-input')?.click()}
                    >
                      <Upload className="h-4 w-4" />
                    </Button>
                  </div>
                  {fileInput && (
                    <div className="mt-2 flex items-center gap-2">
                      <File className="h-4 w-4" />
                      <span className="text-sm text-muted-foreground">
                        {fileInput.name} ({(fileInput.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>

            {/* Expected Hashes */}
            <Card>
                <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5" />
                  Expected Hashes
                </CardTitle>
                  <CardDescription>
                  Provide the expected hash(es) to verify against
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="verification-mode">Verification Mode</Label>
                  <Select value={verificationMode} onValueChange={(value: any) => setVerificationMode(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Hash</SelectItem>
                      <SelectItem value="manifest">Manifest (JSON)</SelectItem>
                    </SelectContent>
                  </Select>
                          </div>

                <div>
                  <Label htmlFor="verification-input">
                    {verificationMode === "single" ? "Expected Hash" : "Manifest JSON"}
                  </Label>
                  <Textarea
                    id="verification-input"
                    placeholder={
                      verificationMode === "single" 
                        ? "Enter expected hash..." 
                        : '{"file.txt": "hash", "file2.txt": "hash2"}'
                    }
                    value={verificationInput}
                    onChange={(e) => setVerificationInput(e.target.value)}
                    className="min-h-[120px] font-mono"
                  />
                      </div>
                </CardContent>
              </Card>
            </div>

          {/* Algorithm Selection (same as generate) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Algorithms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-2">
                {algorithms.map((algo) => (
                  <div key={algo.value} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`verify-${algo.value}`}
                      checked={selectedAlgorithms.includes(algo.value)}
                      onChange={() => handleAlgorithmToggle(algo.value)}
                      className="rounded"
                    />
                    <Label htmlFor={`verify-${algo.value}`} className="flex items-center gap-2">
                      {algo.label}
                      {algo.deprecated && (
                        <Badge variant="destructive" className="text-xs">
                          Legacy
                        </Badge>
                      )}
                    </Label>
                </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Verify Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleVerifyHashes}
              disabled={isProcessing || selectedAlgorithms.length === 0}
              size="lg"
              className="min-w-[200px]"
            >
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Verify Hashes
                </>
              )}
            </Button>
          </div>

          {/* Verification Results */}
          {verificationResults.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Verification Results
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {verificationResults.map((result, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded">
                      <div className="flex items-center gap-3">
                        {result.match ? (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        <div>
                          <div className="font-medium">{result.input}</div>
                          <div className="text-sm text-muted-foreground">{result.algorithm}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{result.computed}</div>
                        <div className="text-xs text-muted-foreground">Expected: {result.expected}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="advanced" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Advanced Options
              </CardTitle>
              <CardDescription>
                Configure advanced processing options for hash generation
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium">Unicode Normalization</h4>
                  <div>
                    <Label htmlFor="unicode-normalization">Normalization Form</Label>
                    <Select value={normalizeUnicode} onValueChange={(value) => setNormalizeUnicode(value as "NFC" | "NFD" | "NFKC" | "NFKD" | "none")}>
                      <SelectTrigger>
                        <SelectValue placeholder="No normalization" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No normalization</SelectItem>
                        <SelectItem value="NFC">NFC (Canonical Composition)</SelectItem>
                        <SelectItem value="NFD">NFD (Canonical Decomposition)</SelectItem>
                        <SelectItem value="NFKC">NFKC (Compatibility Composition)</SelectItem>
                        <SelectItem value="NFKD">NFKD (Compatibility Decomposition)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Text Processing</h4>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="normalize-line-endings"
                        checked={normalizeLineEndings}
                        onCheckedChange={setNormalizeLineEndings}
                      />
                      <Label htmlFor="normalize-line-endings">Normalize Line Endings (LF)</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="strip-bom"
                        checked={stripBOM}
                        onCheckedChange={setStripBOM}
                      />
                      <Label htmlFor="strip-bom">Strip BOM (Byte Order Mark)</Label>
                    </div>
                  </div>
                </div>
                </div>

              <div className="pt-4 border-t">
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">Reset All Settings</h4>
                    <p className="text-sm text-muted-foreground">
                      Clear all inputs and reset to default settings
                    </p>
                </div>
                  <Button variant="outline" onClick={resetForm}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Reset
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}